/**
 * AdminControlRoom — the admin role's per-category control room at
 * `/labeling-overview/:category` (no `:projectId`). Mirrors mockup
 * `#s-actrl` (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html,
 * the corrected v5 layout).
 *
 * CRITICAL ORDERING (spec §5.1, Andrea's explicit fix): sections render in
 * this fixed order — (1) pool-empty banner(s), wrapping ABOVE the content
 * per the workspace banner rule (never a sibling inside a flex-row) → (2)
 * KPI tiles → (3) Projects table → (4) Pending approvals table. Projects
 * MUST sit above Pending approvals; an earlier draft had this reversed.
 *
 * TODO(category): backend must expose project_type on factory Project to
 * scope admin views by category. Until then every registered project is
 * shown here regardless of `categoryKey` — see `ProjectsTable`'s own
 * TODO(category) for the same note. Guessing a mapping from `task` (the
 * only string factory `Project` carries) would be a silently wrong signal,
 * so it's left unfiltered.
 *
 * Register-project opens `RegisterProjectModal` (Task 9).
 */

import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useLabelingRole } from "@/hooks/useLabelingRole";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { getApiBaseUrl } from "../../../../config/api";
import {
  factoryKeys,
  useApproveEnrollmentMutation,
  useFactoryProjectsQuery,
  usePortfolioAnalyticsQuery,
  useSetProjectHiddenMutation,
} from "../../../../hooks/useFactoryQueries";
import type { Enrollment, PoolStatus, Project } from "../../../../types/factory";
import { defaultFactoryTimingWindow } from "../../../../utils/factoryTimingCsv";
import { CATEGORIES, type LabelingCategoryKey } from "../../../../utils/labelingCategories";
import { logger } from "../../../../utils/logger";
import { categoryAccent } from "../categoryAccent";
import { CountStrip, LoadingPanel, SectionLabel } from "../primitives";
import { useEnrollmentsByProject } from "../useEnrollmentsByProject";
import { KpiTiles } from "./KpiTiles";
import { AnalyticsPanel } from "./AnalyticsPanel";
import { PendingApprovalsTable, type PendingApprovalRow } from "./PendingApprovalsTable";
import { ProjectsTable } from "./ProjectsTable";
import { RegisterProjectModal } from "./RegisterProjectModal";

function factoryUrl(path: string): string {
  return `${getApiBaseUrl()}/api/labeling/factory${path}`;
}

/** Same fan-out treatment as `useEnrollmentsByProject`, for `usePoolStatusQuery`. */
function usePoolStatusByProject(projectIds: string[]) {
  return useQueries({
    queries: projectIds.map((pid) => ({
      queryKey: factoryKeys.poolStatus(pid),
      queryFn: async () => {
        const response = await axios.get<PoolStatus>(
          factoryUrl(`/projects/${encodeURIComponent(pid)}/pool-status`),
        );
        return response.data;
      },
      enabled: !!pid,
      retry: 2,
    })),
  });
}

/** Enrollment.phase: 0 = demo, ≥1 = working phases (per annotation-factory
 *  design spec §backend contract). Builds the plain-text Finished/Advances
 *  labels — never a mini-pipeline visualization (spec §5.2). */
function buildPendingApprovalRow(
  project: Project,
  enrollment: Enrollment,
  poolExhaustedKeys: Set<string>,
): PendingApprovalRow {
  const { phase_config } = project;
  const finishedLabel =
    enrollment.phase === 0
      ? `Demo · ${phase_config.demo_total} cases`
      : `Phase ${enrollment.phase} · ${phase_config.working_total} cases`;
  const advancesToLabel = `Phase ${enrollment.phase + 1}`;

  // Enrollment (src/types/factory.ts) doesn't declare a `scorecard` field —
  // Batch does, but this task's data layer only fetches enrollments. Read it
  // defensively so a future backend addition flows through with no rewrite;
  // absence renders "—" downstream, never a fabricated value (no silent
  // fallbacks).
  const scorecard = (enrollment as Enrollment & { scorecard?: { overall_agreement?: number } })
    .scorecard;
  const agreement =
    typeof scorecard?.overall_agreement === "number" ? scorecard.overall_agreement : null;
  const agreementNote =
    agreement == null
      ? undefined
      : enrollment.phase === 0
        ? `vs golden GT · ${phase_config.demo_gt} cases`
        : `peer IAA · ${phase_config.working_overlap} overlap`;

  return {
    project_id: project.project_id,
    annotator: enrollment.annotator,
    finishedLabel,
    advancesToLabel,
    agreement,
    agreementNote,
    poolExhausted: poolExhaustedKeys.has(`${project.project_id}:${enrollment.annotator}`),
  };
}

interface AdminControlRoomProps {
  categoryKey: LabelingCategoryKey;
}

export function AdminControlRoom({ categoryKey }: AdminControlRoomProps) {
  const navigate = useNavigate();
  const role = useLabelingRole();
  const meta = CATEGORIES.find((c) => c.key === categoryKey)!;
  const accent = categoryAccent(categoryKey);

  const projectsQuery = useFactoryProjectsQuery();
  const [timingWindow, setTimingWindow] = useState(defaultFactoryTimingWindow);
  const analyticsQuery = usePortfolioAnalyticsQuery(timingWindow.startDate, timingWindow.endDate);
  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);
  const projectIds = useMemo(() => projects.map((p) => p.project_id), [projects]);

  const enrollmentsResults = useEnrollmentsByProject(projectIds);
  const poolResults = usePoolStatusByProject(projectIds);

  // Distinct from "0 pending approvals" / "0 active annotators" — the
  // per-project enrollments/pool fan-out only starts once `projectsQuery`
  // resolves, so there's a window where projects are loaded but the
  // fan-out is still in flight. `pendingRows` / `activeAnnotatorCount`
  // fall back to `?? []` on undefined per-project data, which would
  // otherwise render "No pending approvals" and 0 counts as final one
  // tick early on every load. With an empty `projectIds` there's nothing
  // to fan out over, so `enrollmentsResults`/`poolResults` are both `[]`
  // and `.some(...)` is vacuously false — an empty projects list still
  // resolves to the real empty state instead of spinning forever. Matches
  // `InternalCategory.tsx`'s `isLoading` gate.
  const isLoading =
    projectsQuery.isLoading ||
    enrollmentsResults.some((r) => r.isLoading) ||
    poolResults.some((r) => r.isLoading);

  const enrollmentsByPid = useMemo(() => {
    const map: Record<string, Enrollment[] | undefined> = {};
    projectIds.forEach((pid, i) => {
      map[pid] = enrollmentsResults[i]?.data;
    });
    return map;
  }, [projectIds, enrollmentsResults]);

  const poolByPid = useMemo(() => {
    const map: Record<string, PoolStatus | undefined> = {};
    projectIds.forEach((pid, i) => {
      map[pid] = poolResults[i]?.data;
    });
    return map;
  }, [projectIds, poolResults]);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [visibilityPendingPids, setVisibilityPendingPids] = useState<Set<string>>(() => new Set());
  const [visibilityErrorPids, setVisibilityErrorPids] = useState<Set<string>>(() => new Set());

  const approveMutation = useApproveEnrollmentMutation();
  const visibilityMutation = useSetProjectHiddenMutation();
  // Keyed by `${project_id}:${annotator}` so a 409 on one row's approve
  // attempt (or its later resolution) never touches another row's inline
  // "pool empty" message. A single nullable string here previously let
  // approving ANY row silently clear every other row's still-exhausted flag.
  const [poolExhaustedKeys, setPoolExhaustedKeys] = useState<Set<string>>(() => new Set());

  const pendingRows = useMemo<PendingApprovalRow[]>(() => {
    const rows: PendingApprovalRow[] = [];
    for (const project of projects) {
      for (const enrollment of enrollmentsByPid[project.project_id] ?? []) {
        if (enrollment.state !== "pending_approval") continue;
        rows.push(buildPendingApprovalRow(project, enrollment, poolExhaustedKeys));
      }
    }
    return rows;
  }, [projects, enrollmentsByPid, poolExhaustedKeys]);

  const blockedProjects = useMemo(
    () =>
      projects.filter((p) =>
        (enrollmentsByPid[p.project_id] ?? []).some((e) => e.state === "blocked_pool_empty"),
      ),
    [projects, enrollmentsByPid],
  );

  const blockedCount = useMemo(
    () =>
      projects.reduce(
        (sum, p) =>
          sum +
          (enrollmentsByPid[p.project_id] ?? []).filter((e) => e.state === "blocked_pool_empty")
            .length,
        0,
      ),
    [projects, enrollmentsByPid],
  );

  const activeAnnotatorCount = useMemo(
    () =>
      projects.reduce(
        (sum, p) =>
          sum + (enrollmentsByPid[p.project_id] ?? []).filter((e) => e.state === "active").length,
        0,
      ),
    [projects, enrollmentsByPid],
  );

  const acceptingCount = projects.filter(
    (p) => (enrollmentsByPid[p.project_id]?.length ?? 0) === 0,
  ).length;

  // Only treat a project as pool-empty once its pool-status query has
  // actually resolved — while poolByPid[pid] is still undefined (loading),
  // this must NOT be conflated with `available: 0`, or the banner flashes
  // falsely on every initial load.
  const poolEmptyBlockedProjects = blockedProjects.filter((p) => {
    const pool = poolByPid[p.project_id];
    return pool !== undefined && pool.available === 0;
  });

  const handleApprove = (row: PendingApprovalRow) => {
    const key = `${row.project_id}:${row.annotator}`;
    setPoolExhaustedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    approveMutation.mutate(
      { pid: row.project_id, annotator: row.annotator },
      {
        onError: (err: unknown) => {
          if ((err as { poolExhausted?: true })?.poolExhausted) {
            setPoolExhaustedKeys((prev) => {
              const next = new Set(prev);
              next.add(key);
              return next;
            });
            return;
          }
          logger.error("[AdminControlRoom] approve failed", err);
        },
      },
    );
  };

  const handleOpenProject = (pid: string) => {
    navigate(`/labeling-overview/${categoryKey}/${encodeURIComponent(pid)}`);
  };

  const handleRegister = () => setRegisterOpen(true);

  const handleSetVisible = async (pid: string, visible: boolean) => {
    setVisibilityPendingPids((prev) => new Set(prev).add(pid));
    setVisibilityErrorPids((prev) => {
      const next = new Set(prev);
      next.delete(pid);
      return next;
    });
    try {
      // Per-call mutate callbacks follow the latest observer. Await each request
      // so overlapping saves settle their own row even when another starts later.
      await visibilityMutation.mutateAsync({ pid, hidden: !visible });
    } catch (err) {
      setVisibilityErrorPids((prev) => new Set(prev).add(pid));
      logger.error("[AdminControlRoom] project visibility update failed", err);
    } finally {
      setVisibilityPendingPids((prev) => {
        const next = new Set(prev);
        next.delete(pid);
        return next;
      });
    }
  };

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <button
          type="button"
          onClick={() => navigate("/labeling-overview")}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← Labeling
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className={`w-0.5 self-stretch rounded-sharp ${accent.edge}`}
            />
            <h1 className="text-2xl font-bold text-fg">{meta.name}</h1>
          </div>
          <div className="mt-3">
            <CountStrip
              items={[
                `${projects.length} projects`,
                `${pendingRows.length} pending approval`,
                ...(blockedCount > 0 ? [`${blockedCount} blocked`] : []),
              ]}
            />
          </div>
        </div>

        {poolEmptyBlockedProjects.map((project) => {
          const blockedHere = (enrollmentsByPid[project.project_id] ?? []).filter(
            (e) => e.state === "blocked_pool_empty",
          ).length;
          return (
            <div
              key={project.project_id}
              data-testid="pool-empty-banner"
              className="mb-4 flex items-start gap-3 rounded border border-warn/30 bg-warn-tint px-4 py-3 text-sm text-warn"
            >
              <span aria-hidden="true">▲</span>
              <span className="flex-1">
                <b>{project.project_id}</b> pool is empty — {blockedHere} annotator
                {blockedHere === 1 ? " is" : "s are"} blocked waiting for cases. Ingest more to
                unblock them.
              </span>
              <button
                type="button"
                onClick={() => handleOpenProject(project.project_id)}
                className="shrink-0 text-xs font-semibold text-warn underline-offset-2 hover:underline"
              >
                Open {project.project_id} →
              </button>
            </div>
          );
        })}

        {isLoading ? (
          <LoadingPanel label="Loading control room…" />
        ) : projectsQuery.error ? (
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load projects
          </div>
        ) : (
          <>
            <KpiTiles
              pendingApprovalCount={pendingRows.length}
              activeAnnotatorCount={activeAnnotatorCount}
              projectCount={projects.length}
              acceptingCount={acceptingCount}
              blockedCount={blockedCount}
              blockedDetail={
                blockedProjects.length > 0
                  ? `${blockedProjects.map((p) => p.project_id).join(", ")} needs cases`
                  : "none blocked"
              }
            />

            <ProjectsTable
              projects={projects}
              poolByPid={poolByPid}
              enrollmentsByPid={enrollmentsByPid}
              onOpen={handleOpenProject}
              onRegister={handleRegister}
              onSetVisible={handleSetVisible}
              visibilityPendingPids={visibilityPendingPids}
              visibilityErrorPids={visibilityErrorPids}
            />

            <SectionLabel eyebrow="Pending approvals" />
            <PendingApprovalsTable rows={pendingRows} onApprove={handleApprove} />

            {role === "admin" && (
              <div className="mt-4">
                {analyticsQuery.isLoading ? (
                  <LoadingPanel label="Loading all-project analytics…" />
                ) : analyticsQuery.error || !analyticsQuery.data ? (
                  <div className="rounded border border-warn/30 bg-warn-tint p-6 text-center text-sm text-warn">
                    Failed to load production analytics
                  </div>
                ) : (
                  <AnalyticsPanel
                    analytics={analyticsQuery.data}
                    scope="portfolio"
                    timingWindow={timingWindow}
                    onTimingWindowChange={setTimingWindow}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      <RegisterProjectModal open={registerOpen} onClose={() => setRegisterOpen(false)} />
    </div>
  );
}

export default AdminControlRoom;
